<template>
    <basic-card>
        <template slot="header">
            {{ $t("cashEvents.listOf") }}
            <context-help help-item-code="cashEventsList"/>
        </template>
        <loader v-if="loadingReconciliation" indefinite :message="$t('cashEvents.loadingReconciliation')"/>
        <framed-content :title="$t('cashEvents.listOf')" :style="{'width' : `${innerWidth}px`}">
            <data-table
              style="overflow-x: auto "
              :data="cashEvents"
              :configuration="cashEventTableConfig"
              @generatePayment="(payload) => $emit('generatePayment', payload)"
              @reconcile="(payload) => $eventBus.$emit('reconcileCashEvent', payload)"
              component-id="cash-events-list"
            />
        </framed-content>
        <framed-content :title="$t('cashEvents.auditInformation')" :style="{'width' : `${innerWidth}px`}">
            <data-table
              style="overflow-x: auto "
              :data="commands"
              :configuration="commandsTableConfig"
              component-id="commands-cash-events-list"
              v-if="commands.length > 0"
            />
            <simple-button @press="fetchCommands">{{ $t("cashEvents.refreshAudit") }}
                &nbsp;<icon :icon="['fal', 'history']"/>
            </simple-button>
        </framed-content>
    </basic-card>
</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import DataTable from "../components/table/DataTable";
import { mapGetters } from "vuex";
import Loader from "../components/Loader";
import ContextHelp from "@/components/help/ContextHelp";
import FramedContent from "@/components/layout/FramedContent";
import SimpleButton from "@/components/button/SimpleButton";

export default {
    name: "CashEventListCard",
    components: {SimpleButton, FramedContent, ContextHelp, Loader, DataTable, BasicCard},
    props: {
        operation: Object,
        cashEvents: Array
    },
    computed: {
        ...mapGetters({
            account: 'banking/account'
        }),
        innerWidth() {
            return window.innerWidth * 0.58;
        }
    },
    mounted() {

    },
    methods: {
        fetchCommands() {
            this.$store.dispatch("commands/GET_COMMANDS", {
                service: "cashflow", query: {
                    parameters: {
                        operation: {
                            type: "entity_reference",
                            operator: "in",
                            value: this.cashEvents.map(ce => ce.operation).join(";")
                        }
                    }
                }
            }).then(data => {
                this.commands = data;
            })
        },
        refreshReconData() {
            this.loadingReconciliation = true;
            if (this.cashEvents.length == 0) {
                this.loadingReconciliation = false;
                return;
            }
            Promise.all(this.cashEvents.map(cashEvent => {
                this.$store.dispatch("reconciliationMatchs/fetchByCashEventId", cashEvent.id)
                  .then(res => {
                      this.$set(this.reconStatuses, cashEvent.id, res);
                  })
            }))
              .then(() => {
                  this.loadingReconciliation = false;
              })
        }
    },
    watch: {
        cashEvents: {
            handler() {
                this.refreshReconData();
            },
            immediate: true
        }
    },
    data: function () {
        return {
            loadingReconciliation: true,
            reconStatuses: {},
            commands: [],
            commandsTableConfig: {
                columns: [
                    {
                        caption: "Id",
                        datum: d => d.id.substring(0, 5)
                    },
                    {
                        caption: "Timestamp",
                        datum: d => this.$formats.formatDateTime(d.timestamp, false)
                    },
                    {
                        caption: "Type",
                        datum: d => {
                            switch (d.commandType) {
                                case "CashEventCreationCommand":
                                    return "Create cash event";
                            }
                        }
                    },
                    {
                        caption: "Amount",
                        datum: d => this.$formats.formatAmount(d.amount / 100)
                    },


                    //       account: "5e4ab197d54d791d96a30a6d"
                    // amount: -5959249920
                    // commandType: "CashEventCreationCommand"
                    // counterAccount: "5f522d196a85643a61a6923f"
                    // counterparty: "5f522d192b63ca36120783f5"
                    // currency: "CZK"
                    // date: "2020-09-15"
                    // id: "5fa1cb8d5503de6a0a6a77aa"
                    // message: null
                    // operation: "5f522d1a13f06c5b7f2c7273"
                    // originOperationId: "5f522d1a13f06c5b7f2c7273"
                    // source: "LS-test"
                    // state: "COMMITTED"
                    // status: "Unpaid"
                    // timestamp: "2020-11-03T22:28:45.732"
                    // transactionReference: "2007"
                ]
            },
            cashEventTableConfig: {
                columns: [
                    {
                        caption: "Id",
                        datum: d => d.id.substring(0, 5)
                    },
                    {
                        caption: this.$t('cashEvents.reconStatus'),
                        datum: d => {
                            if (this.reconStatuses[d.id] == undefined) return "Retrieving data";
                            let numMatches = this.reconStatuses[d.id].length;
                            if (numMatches == 1) {
                                return this.$t('cashEvents.reconciliationStatus.reconciled')
                            } else if (numMatches > 1) {
                                return this.$t('cashEvents.reconciliationStatus.ambiguous', {count: numMatches})
                            }
                            return this.$t('cashEvents.reconciliationStatus.notReconciled')
                        },
                        style: d => {
                            if (this.reconStatuses[d.id] == undefined) return undefined;
                            if (this.reconStatuses[d.id].length == 1) {
                                return {
                                    'background-color': '#3e6545'
                                }
                            }
                        }
                    },
                    {
                        caption: this.$t('cashEvents.plannedDate'),
                        datum: d => this.$formats.formatDate(d.timestamp)
                    },
                    {
                        caption: this.$t('cashEvents.realDate'),
                        datum: d => d.realization != null ? this.$formats.formatDate(d.realization) : "N/A"
                    },
                    {
                        caption: this.$t('cashEvents.amount'),
                        datum: d => this.$formats.formatAmount(d.amount / 100.0)
                    },
                    {
                        caption: this.$t('cashEvents.currency'),
                        datum: d => d.currency
                    },
                    {
                        caption: this.$t('cashEvents.type'),
                        datum: d => d.type
                    },
                    {
                        caption: this.$t('cashEvents.status'),
                        datum: d => d.status ?? "N/A"
                    },
                    {
                        caption: this.$t('cashEvents.account'),
                        datum: d => d.account ? this.account(d.account).name : "N/A"
                    },
                    {
                        caption: this.$t('cashEvents.counterAccount'),
                        datum: d => {
                            if (d.counterAccount != null) {
                                return this.account(d.counterAccount)?.name ?? "N/A";
                            }
                            return "N/A";
                        }
                    },
                    {
                        caption: this.$t('cashEvents.source'),
                        datum: d => d.source
                    },
                    {
                        caption: this.$t('cashEvents.transactionReference'),
                        datum: d => d.transactionReference
                    },
                    {
                        caption: this.$t('cashEvents.category'),
                        datum: d => d.category
                    },
                    {
                        caption: this.$t('cashEvents.subcategory'),
                        datum: d => d.subcategory
                    }
                ],
                actions: [
                    {
                        icon: 'money-bill-alt',
                        name: 'generatePayment',
                        accessLambda: (entry) => entry.amount < 0,
                        tooltip: this.$t('cashEvents.generatePayment')
                    },
                    {
                        icon: 'equals',
                        name: 'reconcile',
                        tooltip: this.$t('cashEvents.reconcile')
                    }
                ]
            }
        }
    }
}
</script>

<style lang="scss" scoped>

</style>